import { Directive, ElementRef, input, OnDestroy, inject, ViewContainerRef, TemplateRef } from '@angular/core';
import { TooltipService, TooltipConfig, TooltipRef } from './tooltip.service';

@Directive({
  selector: '[appTooltip]',
  host: {
    '(mouseenter)': 'onMouseEnter($event)',
    '(focus)': 'onMouseEnter($event)',
    '(mouseleave)': 'onMouseLeave($event)',
    '(blur)': 'onMouseLeave($event)',
    '(click)': 'onClick($event)'
  }
})
export class TooltipDirective implements OnDestroy {
  private elementRef = inject(ElementRef<HTMLElement>);
  private tooltipService = inject(TooltipService);
  private viewContainerRef = inject(ViewContainerRef);

  appTooltip = input<string | TemplateRef<unknown>>('');
  tooltipPosition = input<TooltipConfig['position']>('top');
  tooltipShowDelay = input<number>(100);
  tooltipHideDelay = input<number>(100);
  tooltipDisabled = input<boolean>(false);
  tooltipPanelClass = input<string | string[]>('');
  tooltipMaxWidth = input<string>('200px');

  private tooltipRef: TooltipRef | null = null;
  private showTimeoutId: number | null = null;
  private hideTimeoutId: number | null = null;

  onMouseEnter(event: Event): void {
    if (this.tooltipDisabled() || !this.appTooltip()) {
      return;
    }

    this.clearTimeouts();

    this.showTimeoutId = window.setTimeout(() => {
      this.showTooltip();
    }, this.tooltipShowDelay());
  }

  onMouseLeave(event: Event): void {
    this.clearTimeouts();

    this.hideTimeoutId = window.setTimeout(() => {
      this.hideTooltip();
    }, this.tooltipHideDelay());
  }

  onClick(event: Event): void {
    // Hide tooltip on click to prevent it from staying open
    this.hideTooltip();
  }

  ngOnDestroy(): void {
    this.clearTimeouts();
    this.hideTooltip();
  }

  private showTooltip(): void {
    if (this.tooltipRef?.isOpen()) {
      return;
    }

    const config: TooltipConfig = {
      content: this.appTooltip(),
      position: this.tooltipPosition(),
      panelClass: this.tooltipPanelClass(),
      maxWidth: this.tooltipMaxWidth(),
      closeOnClickOutside: true
    };

    this.tooltipRef = this.tooltipService.show(this.elementRef.nativeElement, config, this.viewContainerRef);
  }

  private hideTooltip(): void {
    if (this.tooltipRef) {
      this.tooltipRef.close();
      this.tooltipRef = null;
    }
  }

  private clearTimeouts(): void {
    if (this.showTimeoutId !== null) {
      clearTimeout(this.showTimeoutId);
      this.showTimeoutId = null;
    }

    if (this.hideTimeoutId !== null) {
      clearTimeout(this.hideTimeoutId);
      this.hideTimeoutId = null;
    }
  }
}
